/**
 * FormIO calculateValue and customConditional Interceptor
 * 
 * This module intercepts FormIO's execution of calculateValue and customConditional
 * to modernize legacy code patterns:
 * 
 * 1. Intercepts fetch() calls to add authentication headers
 * 2. Replaces DOM manipulation (getElementsByName) with FormIO's setValue API
 * 3. Provides comprehensive logging for tracking and migration
 * 4. Handles errors gracefully
 * 
 * Legacy Pattern Being Fixed:
 * - Old forms use fetch() without auth headers
 * - Old forms use document.getElementsByName() for direct DOM manipulation
 * - This breaks in React and lacks proper authentication
 * 
 * Usage: Import and call setupCalculateValueInterceptor() before FormIO forms load
 */

import axios from 'axios';
import config from '../config.js';
import { Formio } from '@formio/js';

/**
 * Track which components have calculateValue/customConditional for migration planning
 */
const calculatedComponents = new Map();
const conditionalComponents = new Map();

/**
 * Setup global fetch interceptor for FormIO calculateValue contexts
 */
export function setupCalculateValueInterceptor() {
    console.log('[FormIO Interceptor] Setting up calculateValue and customConditional interceptor...');
    
    // ===== INTERCEPT FORMIO'S EVALUATE FUNCTION TO CATCH CALCULATEVALUE EXECUTION =====
    // This allows us to see exactly what code is being executed and handle errors gracefully
    if (typeof Formio !== 'undefined' && Formio.Utils && Formio.Utils.evaluate) {
        const originalEvaluate = Formio.Utils.evaluate;
        
        Formio.Utils.evaluate = function(func, args, ret, tokenize) {
            // Check if this looks like calculateValue or customConditional code
            const funcStr = func?.toString() || '';
            const isCalculateValue = funcStr.includes('fetch(') || funcStr.includes('getElementsByName');
            const isCustomConditional = funcStr.includes('show =') || funcStr.includes('return show');
            
            if (isCalculateValue || isCustomConditional) {
                const type = isCalculateValue ? 'calculateValue' : 'customConditional';
                console.group(`[FormIO Interceptor] 🔍 ${type.toUpperCase()} EXECUTION DETECTED`);
                console.log('Type:', type);
                console.log('Function code:', funcStr.substring(0, 500) + (funcStr.length > 500 ? '...' : ''));
                console.log('Arguments:', args);
                console.log('Context:', { ret, tokenize });
                console.groupEnd();
                
                // Try to execute with error handling
                try {
                    const result = originalEvaluate.call(this, func, args, ret, tokenize);
                    console.log(`[FormIO Interceptor] ✅ ${type} executed successfully`);
                    return result;
                } catch (error) {
                    console.error(`[FormIO Interceptor] ❌ ${type} execution failed:`, {
                        error: error.message,
                        stack: error.stack,
                        code: funcStr.substring(0, 200) + '...'
                    });
                    
                    console.warn(`[FormIO Interceptor] 💡 SUGGESTION: Add a React handler for this field to replace the legacy ${type}`);
                    
                    // Return appropriate default based on type
                    if (isCustomConditional) {
                        return true; // Show the field by default
                    }
                    return ret ? '' : undefined;
                }
            }
            
            // For non-calculateValue/customConditional code, use original
            return originalEvaluate.call(this, func, args, ret, tokenize);
        };
        
        console.log('[FormIO Interceptor] ✅ FormIO evaluate function intercepted for calculateValue and customConditional');
    }
    
    // Store original fetch
    const originalFetch = window.fetch;
    
    // Override fetch to add authentication for FormIO contexts
    window.fetch = async function(url, options = {}) {
        const urlStr = typeof url === 'string' ? url : url.toString();
        
        // Check if this is a backend API call (not external resources)
        const isBackendCall = urlStr.includes(config.BackendUrl) || 
                             urlStr.startsWith('http://localhost') || 
                             urlStr.startsWith('/admin/');
        
        if (isBackendCall) {
            console.log('[FormIO Interceptor] 🔧 Intercepting fetch call from calculateValue:', {
                url: urlStr,
                originalOptions: options,
                timestamp: new Date().toISOString()
            });
            
            // Ensure options.headers exists
            if (!options.headers) {
                options.headers = {};
            }
            
            // Add authentication headers
            const token = localStorage.getItem('token');
            if (token && !options.headers['Authorization']) {
                options.headers['Authorization'] = `Bearer ${token}`;
            }
            
            // Add language header
            const lang = localStorage.getItem('lang') || 'en';
            if (!options.headers['lang']) {
                options.headers['lang'] = lang;
            }
            
            // Add other standard headers
            if (!options.headers['Accept']) {
                options.headers['Accept'] = 'application/json';
            }
            
            if (!options.headers['X-Requested-With']) {
                options.headers['X-Requested-With'] = 'XMLHttpRequest';
            }
            
            // Ensure credentials are included
            if (!options.credentials) {
                options.credentials = 'include';
            }
            
            console.log('[FormIO Interceptor] ✅ Added authentication headers to fetch:', {
                url: urlStr,
                headers: options.headers
            });
        }
        
        // Call original fetch with enhanced options
        return originalFetch.call(this, url, options);
    };
    
    // Override getElementsByName to provide safer field updates for legacy code
    const originalGetElementsByName = document.getElementsByName.bind(document);
    document.getElementsByName = function(name) {
        const elements = originalGetElementsByName(name);
        
        // Wrap the elements to catch errors when setting values
        if (elements.length > 0 && name.startsWith('data[')) {
            const originalElement = elements[0];
            
            // Create a proxy to intercept value sets
            const handler = {
                set: function(target, property, value) {
                    if (property === 'value') {
                        try {
                            console.log('[FormIO Interceptor] 📝 Legacy code setting field:', {
                                fieldName: name,
                                value: value,
                                timestamp: new Date().toISOString()
                            });
                            
                            target.value = value;
                            
                            // Trigger events for FormIO to pick up the change
                            target.dispatchEvent(new Event('input', { bubbles: true }));
                            target.dispatchEvent(new Event('change', { bubbles: true }));
                            
                            return true;
                        } catch (error) {
                            console.error('[FormIO Interceptor] ❌ Error setting field value via DOM:', {
                                fieldName: name,
                                error: error.message
                            });
                            return false;
                        }
                    }
                    return Reflect.set(target, property, value);
                }
            };
            
            // Note: We can't actually return a proxy here as it breaks the NodeList
            // But we can at least log the access
            console.log('[FormIO Interceptor] 📋 Legacy code accessing field via getElementsByName:', name);
        }
        
        return elements;
    };
    
    // Wrap console errors to catch calculateValue issues
    const originalConsoleError = console.error;
    console.error = function(...args) {
        const errorStr = args.join(' ');
        
        // Detect common calculateValue errors
        if (errorStr.includes('hasOwnProperty') || 
            errorStr.includes('Cannot read properties of undefined')) {
            console.warn('[FormIO Interceptor] 🚨 Detected calculateValue error - legacy code may need React handler:', {
                error: errorStr,
                suggestion: 'Consider adding a React handler for this field in Add.jsx',
                timestamp: new Date().toISOString()
            });
        }
        
        return originalConsoleError.apply(console, args);
    };
    
    console.log('[FormIO Interceptor] ✅ Fetch interceptor and error handling installed');
}

/**
 * Helper to find a FormIO component by field name and set its value
 * This replaces the legacy getElementsByName pattern
 */
export function setFormIOFieldValue(form, fieldName, value) {
    if (!form) {
        console.warn('[FormIO Interceptor] Cannot set field value - form instance not available');
        return false;
    }
    
    // Remove 'data[' prefix and ']' suffix if present (legacy format)
    const cleanFieldName = fieldName.replace(/^data\[/, '').replace(/\]$/, '');
    
    console.log('[FormIO Interceptor] 🎯 Setting field value:', {
        originalFieldName: fieldName,
        cleanFieldName: cleanFieldName,
        value: value
    });
    
    try {
        // Try to get the component
        const component = form.getComponent(cleanFieldName);
        
        if (component) {
            component.setValue(value);
            console.log('[FormIO Interceptor] ✅ Field value set successfully:', cleanFieldName);
            return true;
        } else {
            console.warn('[FormIO Interceptor] ⚠️ Component not found:', cleanFieldName);
            
            // Fallback: try direct DOM manipulation (last resort)
            const element = document.getElementsByName(`data[${cleanFieldName}]`)[0];
            if (element) {
                element.value = value;
                // Trigger change event for FormIO to pick up the change
                element.dispatchEvent(new Event('input', { bubbles: true }));
                element.dispatchEvent(new Event('change', { bubbles: true }));
                console.log('[FormIO Interceptor] ⚠️ Used fallback DOM manipulation for:', cleanFieldName);
                return true;
            }
            
            return false;
        }
    } catch (error) {
        console.error('[FormIO Interceptor] ❌ Error setting field value:', {
            fieldName: cleanFieldName,
            error: error.message
        });
        return false;
    }
}

/**
 * Track a component that uses calculateValue
 */
export function trackCalculatedComponent(componentKey, componentLabel, module, calculateValueCode) {
    const key = `${module}__${componentKey}`;
    
    if (!calculatedComponents.has(key)) {
        const info = {
            module: module,
            componentKey: componentKey,
            componentLabel: componentLabel,
            calculateValueCode: calculateValueCode,
            firstSeen: new Date().toISOString(),
            executionCount: 0
        };
        
        calculatedComponents.set(key, info);
        
        console.log('[FormIO Interceptor] 📊 New calculateValue component tracked:', {
            module: module,
            component: componentKey,
            label: componentLabel,
            codePreview: calculateValueCode?.substring(0, 100) + '...'
        });
    }
    
    // Increment execution count
    const info = calculatedComponents.get(key);
    info.executionCount++;
    info.lastExecuted = new Date().toISOString();
}

/**
 * Track a component that uses customConditional
 */
export function trackConditionalComponent(componentKey, componentLabel, module, conditionalCode) {
    const key = `${module}__${componentKey}`;
    
    if (!conditionalComponents.has(key)) {
        const info = {
            module: module,
            componentKey: componentKey,
            componentLabel: componentLabel,
            conditionalCode: conditionalCode,
            firstSeen: new Date().toISOString(),
            executionCount: 0
        };
        
        conditionalComponents.set(key, info);
        
        console.log('[FormIO Interceptor] 📊 New customConditional component tracked:', {
            module: module,
            component: componentKey,
            label: componentLabel,
            codePreview: conditionalCode?.substring(0, 100) + '...'
        });
    }
    
    // Increment execution count
    const info = conditionalComponents.get(key);
    info.executionCount++;
    info.lastExecuted = new Date().toISOString();
}

/**
 * Get tracking reports for migration planning
 */
export function getTrackingReports() {
    console.group('[FormIO Interceptor] 📊 TRACKING REPORT');
    
    console.log('\n=== Components with calculateValue ===');
    console.table(Array.from(calculatedComponents.values()));
    
    console.log('\n=== Components with customConditional ===');
    console.table(Array.from(conditionalComponents.values()));
    
    console.groupEnd();
    
    return {
        calculatedComponents: Array.from(calculatedComponents.values()),
        conditionalComponents: Array.from(conditionalComponents.values())
    };
}

/**
 * Make tracking reports available globally for debugging
 */
if (typeof window !== 'undefined') {
    window.getFormIOTrackingReport = getTrackingReports;
    console.log('[FormIO Interceptor] 💡 Run window.getFormIOTrackingReport() in console to see all components using calculateValue/customConditional');
}

export default setupCalculateValueInterceptor;
